/**
 * Glass Box Cache — IndexedDB persistence for observability events (Spec 167)
 *
 * Per-stream cache of Glass Box events that survives page refresh and
 * WebSocket reconnect. Mirrors the streamCache.ts pattern.
 *
 * Architecture:
 *   WebSocket events → in-memory Map (useGlassBoxEvents) → debounced write → IndexedDB
 *   On mount / stream switch → read from IndexedDB → populate in-memory Map
 */

import { get, set, del, keys } from 'idb-keyval';
import type { GlassBoxItem } from '../hooks/useGlassBoxEvents';

const PREFIX = 'glassbox:';
const CACHE_VERSION = 1;
const MAX_CACHE_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours
const MAX_CACHED_STREAMS = 10; // LRU eviction threshold
const MAX_EVENTS_PER_STREAM = 500;

interface CachedGlassBox {
  version: number;
  events: GlassBoxItem[];
  cachedAt: string;
  accessedAt: string;
}

/**
 * Load Glass Box events from IndexedDB for a stream.
 * Returns null if not cached, expired, or version mismatch.
 */
export async function loadGlassBoxCache(streamId: string): Promise<GlassBoxItem[] | null> {
  try {
    const cached = await get<CachedGlassBox>(`${PREFIX}${streamId}`);
    if (!cached || cached.version !== CACHE_VERSION) return null;

    const age = Date.now() - new Date(cached.cachedAt).getTime();
    if (age > MAX_CACHE_AGE_MS) {
      await del(`${PREFIX}${streamId}`);
      return null;
    }

    // Update access time for LRU
    cached.accessedAt = new Date().toISOString();
    await set(`${PREFIX}${streamId}`, cached);

    return cached.events;
  } catch {
    return null;
  }
}

/**
 * Save Glass Box events to IndexedDB for a stream.
 * Caps at MAX_EVENTS_PER_STREAM (keeps latest).
 */
export async function saveGlassBoxCache(streamId: string, events: GlassBoxItem[]): Promise<void> {
  try {
    const capped = events.length > MAX_EVENTS_PER_STREAM
      ? events.slice(events.length - MAX_EVENTS_PER_STREAM)
      : events;

    const now = new Date().toISOString();
    const cached: CachedGlassBox = {
      version: CACHE_VERSION,
      events: capped,
      cachedAt: now,
      accessedAt: now,
    };

    await set(`${PREFIX}${streamId}`, cached);
    await enforceLRU();
  } catch {
    // Fail silently — cache is best-effort
  }
}

/**
 * Clear Glass Box cache for a specific stream.
 */
export async function clearGlassBoxCache(streamId: string): Promise<void> {
  try {
    await del(`${PREFIX}${streamId}`);
  } catch {
    // Fail silently
  }
}

/**
 * Enforce LRU limit — remove least recently accessed streams.
 */
async function enforceLRU(): Promise<void> {
  try {
    const allKeys = await keys();
    const gbKeys = allKeys.filter(
      key => typeof key === 'string' && key.startsWith(PREFIX)
    );

    if (gbKeys.length <= MAX_CACHED_STREAMS) return;

    const entries: Array<{ key: string; accessedAt: string }> = [];
    for (const key of gbKeys) {
      const cached = await get<CachedGlassBox>(key as string);
      if (cached) {
        entries.push({ key: key as string, accessedAt: cached.accessedAt });
      }
    }

    entries.sort((a, b) => new Date(a.accessedAt).getTime() - new Date(b.accessedAt).getTime());
    const toRemove = entries.slice(0, entries.length - MAX_CACHED_STREAMS);
    for (const { key } of toRemove) {
      await del(key);
    }
  } catch {
    // Fail silently
  }
}
